import type {RequirementOf} from '../../lib/types';
import {FLOW_WIDTH_MAP} from '../../lib/preview-geometry';
import {InlineLabel} from '../InlineLabel';
import {type ElemProps} from './element-props';
import {buildTextStyle} from './element-styles';
import {labelText} from '../../lib/label-text';
import {textFormatOf} from '../../lib/text-format';

export function HeaderRenderer({
  item,
  label,
  fillMode: _fillMode,
  inlineEdit,
  sampleData,
}: ElemProps & {item: RequirementOf<'header'>}) {
  const s = item.style;
  // Styles on part of the label (DERP-307), and what the rest of it inherits.
  const value = labelText(item);
  const base = textFormatOf(s);
  const widthStyle =
    item.flow_width && item.flow_width !== 'full' ? {width: FLOW_WIDTH_MAP[item.flow_width]} : {};
  const hCfg = item.config;
  const hTextStyle = buildTextStyle(s, {fontSize: '0.875rem', fontWeight: '400'});
  const hPrefix =
    hCfg.list_type === 'unordered' ? '• ' : hCfg.list_type === 'ordered' ? '1. ' : undefined;
  const displayType = hCfg.display_type ?? 'text';

  const formatValue = () => {
    if (!hCfg.value) return null;
    if (displayType === 'date') {
      const d = new Date(hCfg.value + 'T00:00');
      return isNaN(d.getTime()) ? hCfg.value : d.toLocaleDateString();
    }
    if (displayType === 'time') return hCfg.value;
    if (displayType === 'datetime') {
      const d = new Date(hCfg.value);
      return isNaN(d.getTime()) ? hCfg.value : d.toLocaleString();
    }
    return null;
  };

  const formatted = formatValue();

  return (
    <div style={{...widthStyle, opacity: s.opacity ?? undefined}} className="py-1">
      {item.section && (
        <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-0.5">{item.section}</p>
      )}
      {displayType === 'text' ? (
        <InlineLabel
          value={value}
          sampleData={sampleData ?? {}}
          base={base}
          itemId={item.id}
          className="text-sm block"
          style={hTextStyle}
          prefix={hPrefix}
          inlineEdit={inlineEdit}
        />
      ) : (
        <div className="flex items-baseline gap-2">
          {label && (
            <InlineLabel
              value={value}
              sampleData={sampleData ?? {}}
              base={base}
              itemId={item.id}
              className="text-sm shrink-0"
              style={hTextStyle}
              inlineEdit={inlineEdit}
            />
          )}
          <span style={hTextStyle} className="text-sm">
            {formatted ?? (
              <span className="text-gray-400 italic text-xs">
                {displayType === 'date' ? 'Date' : displayType === 'time' ? 'Time' : 'Date & time'}
              </span>
            )}
          </span>
        </div>
      )}
    </div>
  );
}
